import { RichTextEditor } from '@/components/RichTextEditor/RichTextEditor';
import { ChipSelect } from '@/dsm-components';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PalmettoIcon } from '@palmetto/dsm-components';
import { useCallback, useMemo, useRef } from 'react';
import {
  useWatch,
  useFieldArray,
  Controller,
  Control,
  ControllerProps,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';

import {
  ChecklistConditionNameLabels,
  ChecklistConditionOperator,
  ChecklistEvaluationKey,
  ChecklistItemInputType,
  ChecklistReferenceKey,
  ChecklistReferenceKeyLabels,
} from 'types';

import {
  DialogFormValues,
  evaluationKeyOptions,
  inputTypeOptions,
  referenceKeyOptions,
} from './checklistItemInputOptions';
import { ConditionChip } from './ConditionChip';
import { EvaluationParamsEditor } from './EvaluationParamsEditor';
import { exposesDecisionOptions, stripOptionFlagMappings } from './flagMappingOptions';
import { InlineEditableLabelInput } from './InlineEditableLabelInput';
import { InputOptionsEditor } from './InputOptionsEditor';

export interface InputFieldRowProps {
  id: string;
  inputIndex: number;
  control: Control<DialogFormValues>;
  getValues: UseFormGetValues<DialogFormValues>;
  setValue: UseFormSetValue<DialogFormValues>;
  removeInput: (index: number) => void;
  flagSettingOptions: { label: string; value: string }[];
  programTypes: string[];
}

export const InputFieldRow = ({
  id,
  inputIndex,
  control,
  getValues,
  setValue,
  removeInput,
  flagSettingOptions,
  programTypes,
}: InputFieldRowProps) => {
  const sortable = useSortable({ id });
  // oxlint-disable-next-line react-perf/jsx-no-new-object-as-prop
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.5 : 1,
  };

  const watchedType = useWatch({ control, name: `inputs.${inputIndex}.type` });
  const watchedLabel = useWatch({ control, name: `inputs.${inputIndex}.label` });
  const watchedRequired = useWatch({ control, name: `inputs.${inputIndex}.required` });
  const watchedReferenceKey = useWatch({ control, name: `inputs.${inputIndex}.referenceKey` });
  const watchedFlagSettingId = useWatch({ control, name: `inputs.${inputIndex}.requirementFlagSettingId` });
  const watchedEvaluationKey = useWatch({ control, name: `inputs.${inputIndex}.evaluationKey` });
  const isReferenceValue = watchedType === ChecklistItemInputType.referenceValue;
  const isEvaluation = watchedType === ChecklistItemInputType.evaluation;
  const showsDecisionOptions = exposesDecisionOptions(watchedType, watchedEvaluationKey);
  const labelInputRef = useRef<HTMLInputElement>(null);

  const clearOptionFlagMappings = useCallback(() => {
    const options = getValues(`inputs.${inputIndex}.options`) ?? [];
    if (!options.some((o) => o.flagMappings?.length || o.requirementFlagSettingId)) return;
    setValue(`inputs.${inputIndex}.options`, options.map(stripOptionFlagMappings));
  }, [getValues, setValue, inputIndex]);

  const handleTypeChange = useCallback(
    (newType: string) => {
      setValue(`inputs.${inputIndex}.type`, newType as ChecklistItemInputType);
      if (newType !== ChecklistItemInputType.requirement) {
        clearOptionFlagMappings();
      }
      if (newType !== ChecklistItemInputType.referenceValue) {
        setValue(`inputs.${inputIndex}.referenceKey`, undefined);
      }
      if (newType !== ChecklistItemInputType.requirement && newType !== ChecklistItemInputType.evaluation) {
        setValue(`inputs.${inputIndex}.requirementFlagSettingId`, undefined);
      }
      if (newType !== ChecklistItemInputType.evaluation) {
        setValue(`inputs.${inputIndex}.evaluationKey`, undefined);
        setValue(`inputs.${inputIndex}.evaluationParams`, undefined);
      }
      if (newType === ChecklistItemInputType.requirement) {
        setValue(`inputs.${inputIndex}.required`, true);
      }
    },
    [setValue, inputIndex, clearOptionFlagMappings],
  );

  const handleEvaluationKeyChange = useCallback(
    (newKey: string) => {
      setValue(`inputs.${inputIndex}.evaluationKey`, (newKey || undefined) as ChecklistEvaluationKey | undefined);
      // Params are key-specific; clear them so a stale tolerance map from the previous
      // evaluation type cannot carry over into an incompatible one.
      setValue(`inputs.${inputIndex}.evaluationParams`, undefined);
      if (!exposesDecisionOptions(ChecklistItemInputType.evaluation, newKey)) {
        clearOptionFlagMappings();
      }
    },
    [setValue, inputIndex, clearOptionFlagMappings],
  );

  const handleFlagSettingChange = useCallback(
    (newId: string) => {
      setValue(`inputs.${inputIndex}.requirementFlagSettingId`, newId || undefined);
    },
    [setValue, inputIndex],
  );

  const handleReferenceKeyChange = useCallback(
    (newKey: string) => {
      setValue(`inputs.${inputIndex}.referenceKey`, newKey as ChecklistReferenceKey);
      if (!watchedLabel?.trim()) {
        const defaultLabel = ChecklistReferenceKeyLabels[newKey as ChecklistReferenceKey];
        setValue(`inputs.${inputIndex}.label`, defaultLabel);
        if (labelInputRef.current) labelInputRef.current.value = defaultLabel;
      }
    },
    [setValue, inputIndex, watchedLabel],
  );

  const handleToggleRequired = useCallback(() => {
    setValue(`inputs.${inputIndex}.required`, !watchedRequired);
  }, [setValue, inputIndex, watchedRequired]);

  const handleRemove = useCallback(() => {
    removeInput(inputIndex);
  }, [removeInput, inputIndex]);

  const {
    fields: conditionFields,
    append: appendCondition,
    remove: removeCondition,
  } = useFieldArray({ control, name: `inputs.${inputIndex}.conditions` });

  const handleAddCondition = useCallback(() => {
    appendCondition({ name: '', operator: ChecklistConditionOperator.Is, paramsRaw: '' });
  }, [appendCondition]);

  const canAddCondition = conditionFields.length < Object.keys(ChecklistConditionNameLabels).length;

  const handleLabelCommit = useCallback(
    (value: string) => {
      setValue(`inputs.${inputIndex}.label`, value);
    },
    [setValue, inputIndex],
  );

  const flagRequirementOptions = useMemo(
    () => [{ label: 'No Flag Requirement', value: '' }, ...flagSettingOptions],
    [flagSettingOptions],
  );

  const renderTooltipText = useCallback<ControllerProps<DialogFormValues, `inputs.${number}.tooltipText`>['render']>(
    ({ field }) => (
      <RichTextEditor
        label="Tooltip Text"
        value={field.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
        helperText="Guidance shown to QC users for this requirement during review"
      />
    ),
    [],
  );

  return (
    <div ref={sortable.setNodeRef} style={style} className="mb-8">
      <div className="flex flex-wrap items-center gap-8">
        <div
          {...sortable.attributes}
          {...sortable.listeners}
          className="shrink-0 rotate-90 cursor-grab touch-none text-text-tertiary"
        >
          <PalmettoIcon name="dragReorderDots" size="xs" />
        </div>
        <ChipSelect
          searchable
          options={inputTypeOptions}
          value={watchedType}
          onChange={handleTypeChange}
          placeholder="Type"
          variant="volt"
        />
        <PalmettoIcon name="chevronRight" size="xs" className="text-text-tertiary" />
        <InlineEditableLabelInput
          ref={labelInputRef}
          variant="chip"
          defaultValue={watchedLabel}
          placeholder="Label"
          onCommit={handleLabelCommit}
        />
        {isReferenceValue && (
          <ChipSelect
            searchable
            options={referenceKeyOptions}
            value={watchedReferenceKey ?? ''}
            onChange={handleReferenceKeyChange}
            placeholder="Reference Key"
            variant="lumen"
            pinnedOptionValue={''}
          />
        )}
        {(watchedType === ChecklistItemInputType.requirement || isEvaluation) && (
          <ChipSelect
            searchable
            options={flagRequirementOptions}
            value={watchedFlagSettingId ?? ''}
            onChange={handleFlagSettingChange}
            placeholder="Flag Requirement"
            variant="lumen"
            pinnedOptionValue={''}
          />
        )}
        {isEvaluation && (
          <ChipSelect
            options={evaluationKeyOptions}
            value={watchedEvaluationKey ?? ''}
            onChange={handleEvaluationKeyChange}
            placeholder="Evaluation"
            variant="volt"
          />
        )}
        {!isReferenceValue && !isEvaluation && watchedType !== ChecklistItemInputType.requirement && (
          <button
            type="button"
            onClick={handleToggleRequired}
            className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-12 py-4 text-sm leading-none font-medium whitespace-nowrap ${watchedRequired ? 'border-tag-container-default-palmetto bg-tag-container-default-palmetto text-tag-text-default-palmetto' : 'border-chip-border-default-idle bg-chip-canvas-default-idle text-text-tertiary'}`}
          >
            {watchedRequired ? 'Required' : 'Optional'}
          </button>
        )}
        <button
          type="button"
          onClick={handleRemove}
          className="cursor-pointer border-none bg-transparent p-0 leading-none text-text-tertiary hover:text-text-primary"
          aria-label="Remove input"
        >
          <PalmettoIcon name="x" size="xs" />
        </button>
      </div>
      <div className="ml-4">
        <Controller name={`inputs.${inputIndex}.tooltipText`} control={control} render={renderTooltipText} />
      </div>
      {conditionFields.map((conditionField, conditionIndex) => (
        <div key={conditionField.id} className="mt-4 ml-4 flex items-center gap-4">
          <span className={`font-medium body-300 ${conditionIndex === 0 ? 'invisible' : 'text-text-tertiary'}`}>
            and
          </span>
          <ConditionChip
            fieldPrefix={`inputs.${inputIndex}.conditions`}
            conditionIndex={conditionIndex}
            control={control}
            getValues={getValues}
            setValue={setValue}
            removeCondition={removeCondition}
            programTypes={programTypes}
          />
        </div>
      ))}
      {canAddCondition && (
        <div className="mt-4">
          <button
            type="button"
            onClick={handleAddCondition}
            className="text-interactive-primary hover:text-interactive-primary-hover inline-flex cursor-pointer items-center gap-4 border-none bg-transparent p-0 text-sm leading-none font-medium"
          >
            <PalmettoIcon name="add" size="xs" />
            Condition
          </button>
        </div>
      )}
      {showsDecisionOptions && (
        <div className="mt-4 ml-4">
          <InputOptionsEditor inputIndex={inputIndex} control={control} setValue={setValue} />
        </div>
      )}
      {isEvaluation && (
        <div className="ml-4">
          <EvaluationParamsEditor
            inputIndex={inputIndex}
            control={control}
            setValue={setValue}
            evaluationKey={watchedEvaluationKey}
          />
        </div>
      )}
    </div>
  );
};
